/**
 * Financial Forecasting Panel Component
 * LivePlan-style financial projections with what-if scenarios
 * Completely optional - doesn't modify plan data
 */

import React, { useState, useEffect } from 'react';
import { BusinessPlan } from '../../types/businessPlan';
import {
  FinancialForecast,
  ForecastCalculationInput,
  RevenueStream,
  ExpenseCategory,
  TimelineType,
  DEFAULT_FINANCIAL_ASSUMPTIONS,
  ScenarioType
} from '../../types/financialForecasting';
import {
  calculateFinancialForecast,
  getForecast,
  saveForecast
} from '../../services/financialForecastService';
import { supabase } from '../../lib/supabaseClient';
import { toast } from 'react-hot-toast';
import RevenueChart from './charts/RevenueChart';
import ExpenseBreakdownChart from './charts/ExpenseBreakdownChart';
import ProfitChart from './charts/ProfitChart';
import MetricsDashboard from './charts/MetricsDashboard';

interface FinancialForecastingPanelProps {
  plan: BusinessPlan;
}

const FinancialForecastingPanel: React.FC<FinancialForecastingPanelProps> = ({ plan }) => {
  const [forecast, setForecast] = useState<FinancialForecast | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeScenario, setActiveScenario] = useState<ScenarioType>('realistic');
  const [activeTab, setActiveTab] = useState<'projections' | 'metrics' | 'benchmarks'>('projections');
  const [viewMode, setViewMode] = useState<'charts' | 'table'>('charts');
  const [tableView, setTableView] = useState<'summary' | 'pnl' | 'cashflow' | 'balance_sheet'>('summary');
  const [selectedRevenueModel, setSelectedRevenueModel] = useState<'unit_sales' | 'subscription' | 'billable_hours' | 'occupancy' | ''>('');
  const [horizonPreset, setHorizonPreset] = useState<'months_36' | 'monthly12_yearly' | 'custom'>('months_36');
  const [yearsAfterMonth12, setYearsAfterMonth12] = useState<number>(3);
  
  // Form state for new forecast
  const [timeline, setTimeline] = useState<TimelineType>('monthly');
  const [periodsCount, setPeriodsCount] = useState(36); // 3 years monthly
  const [assumptions, setAssumptions] = useState(DEFAULT_FINANCIAL_ASSUMPTIONS);
  const [revenueStreams, setRevenueStreams] = useState<RevenueStream[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[]>([]);

  useEffect(() => {
    if (horizonPreset === 'months_36') {
      setTimeline('monthly');
      setPeriodsCount(36);
    }
    if (horizonPreset === 'monthly12_yearly') {
      setTimeline('mixed');
      const years = Math.min(5, Math.max(3, yearsAfterMonth12));
      setYearsAfterMonth12(years);
      setPeriodsCount(12 + years);
    }
  }, [horizonPreset, yearsAfterMonth12]);

  const formatForecastHorizon = (f: FinancialForecast) => {
    if (f.timeline === 'monthly' && f.periodsCount === 36) return '36 months (monthly)';
    if (f.timeline === 'mixed') {
      const years = Math.max(0, (f.periodsCount || 0) - 12);
      return `12 months (monthly) + ${years} years (yearly)`;
    }
    return `${f.periodsCount} ${f.timeline} periods`;
  };

  const updateCapexItem = (index: number, updates: any) => {
    const items = Array.isArray((assumptions as any).capexItems) ? [...((assumptions as any).capexItems as any[])] : [];
    items[index] = { ...items[index], ...updates };
    setAssumptions({ ...(assumptions as any), capexItems: items } as any);
  };

  const addCapexItem = () => {
    const items = Array.isArray((assumptions as any).capexItems) ? [...((assumptions as any).capexItems as any[])] : [];
    items.push({
      id: `capex-${Date.now()}`,
      name: 'New asset',
      amount: 0,
      purchasePeriodNumber: 1,
      usefulLifeMonths: 36,
    });
    setAssumptions({ ...(assumptions as any), capexItems: items } as any);
  };

  const removeCapexItem = (index: number) => {
    const items = Array.isArray((assumptions as any).capexItems) ? [...((assumptions as any).capexItems as any[])] : [];
    items.splice(index, 1);
    setAssumptions({ ...(assumptions as any), capexItems: items } as any);
  };

  const getTableRows = (f: FinancialForecast, scenario: ScenarioType) => {
    const rows = f.projections[scenario] || [];
    if (f.timeline === 'mixed') return rows;
    return rows.slice(0, 12);
  };

  const downloadCsv = (filename: string, csv: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const buildPnLCsv = (f: FinancialForecast, scenario: ScenarioType) => {
    const rows = f.projections[scenario] || [];
    const header = ['period', 'revenue', 'expenses', 'gross_profit', 'tax', 'net_profit'];
    const lines = [header.join(',')];
    for (const r of rows) {
      const tax = ((r.grossProfit ?? 0) - (r.interestExpense ?? 0)) - (r.netProfit ?? 0);
      const expenses = (r.totalExpenses ?? 0) + (r.depreciationExpense ?? 0);
      lines.push([
        JSON.stringify(r.period),
        String(r.totalRevenue ?? 0),
        String(expenses),
        String(r.grossProfit ?? 0),
        String(Math.round(tax)),
        String(r.netProfit ?? 0),
      ].join(','));
    }
    return lines.join('\n');
  };

  const buildBalanceSheetCsv = (f: FinancialForecast, scenario: ScenarioType) => {
    const rows = f.projections[scenario] || [];
    const header = ['period', 'cash', 'total_assets', 'total_liabilities', 'total_equity', 'tie_out'];
    const lines = [header.join(',')];
    for (const r of rows) {
      lines.push([
        JSON.stringify(r.period),
        String(r.cashBalance ?? 0),
        String(r.totalAssets ?? 0),
        String(r.totalLiabilities ?? 0),
        String(r.totalEquity ?? 0),
        String(r.balanceSheetTieOut ?? 0),
      ].join(','));
    }
    return lines.join('\n');
  };

  const buildCashflowCsv = (f: FinancialForecast, scenario: ScenarioType) => {
    const rows = f.projections[scenario] || [];
    const header = ['period', 'cash_flow', 'cumulative_cash_flow'];
    const lines = [header.join(',')];
    for (const r of rows) {
      lines.push([
        JSON.stringify(r.period),
        String(r.cashFlow ?? 0),
        String(r.cumulativeCashFlow ?? 0),
      ].join(','));
    }
    return lines.join('\n');
  };

  const applyRevenueModelTemplate = (templateId: typeof selectedRevenueModel) => {
    if (!templateId) return;

    const baseAssumptions = {
      ...DEFAULT_FINANCIAL_ASSUMPTIONS,
      fixedCosts: 5000,
      variableCostPercentage: 30,
      taxRate: 25,
      investmentAmount: 25000,
    };

    if (templateId === 'unit_sales') {
      setAssumptions({
        ...baseAssumptions,
        variableCostPercentage: 35,
      });
      setRevenueStreams([
        {
          id: 'revenue-unit-sales',
          name: 'Unit sales',
          category: 'product',
          projections: [2000],
          growthRate: 12,
          unitPrice: 50,
          unitsPerPeriod: 40,
        },
      ]);
      setExpenseCategories([
        { id: 'expense-rent', name: 'Rent', category: 'fixed', projections: [1500], growthRate: 2 },
        { id: 'expense-marketing', name: 'Marketing', category: 'variable', projections: [800], growthRate: 8 },
        { id: 'expense-tools', name: 'Tools & software', category: 'fixed', projections: [300], growthRate: 2 },
      ]);
      return;
    }

    if (templateId === 'subscription') {
      setAssumptions({
        ...baseAssumptions,
        variableCostPercentage: 20,
      });
      setRevenueStreams([
        {
          id: 'revenue-subscription',
          name: 'Subscriptions',
          category: 'subscription',
          projections: [3000],
          growthRate: 18,
          unitPrice: 30,
          unitsPerPeriod: 100,
        },
      ]);
      setExpenseCategories([
        { id: 'expense-hosting', name: 'Hosting', category: 'variable', projections: [400], growthRate: 10 },
        { id: 'expense-sales', name: 'Sales & marketing', category: 'variable', projections: [1200], growthRate: 12 },
        { id: 'expense-admin', name: 'Admin', category: 'fixed', projections: [600], growthRate: 2 },
      ]);
      return;
    }

    if (templateId === 'billable_hours') {
      setAssumptions({
        ...baseAssumptions,
        variableCostPercentage: 15,
      });
      setRevenueStreams([
        {
          id: 'revenue-billable-hours',
          name: 'Billable hours',
          category: 'service',
          projections: [8000],
          growthRate: 10,
          unitPrice: 100,
          unitsPerPeriod: 80,
        },
      ]);
      setExpenseCategories([
        { id: 'expense-contractors', name: 'Contractors', category: 'variable', projections: [2000], growthRate: 8 },
        { id: 'expense-tools', name: 'Tools & software', category: 'fixed', projections: [300], growthRate: 2 },
        { id: 'expense-marketing', name: 'Marketing', category: 'variable', projections: [600], growthRate: 8 },
      ]);
      return;
    }

    if (templateId === 'occupancy') {
      setAssumptions({
        ...baseAssumptions,
        variableCostPercentage: 40,
        fixedCosts: 9000,
        investmentAmount: 50000,
      });
      setRevenueStreams([
        {
          id: 'revenue-occupancy',
          name: 'Occupancy revenue',
          category: 'service',
          projections: [12000],
          growthRate: 8,
          unitPrice: 150,
          unitsPerPeriod: 80,
        },
      ]);
      setExpenseCategories([
        { id: 'expense-lease', name: 'Lease', category: 'fixed', projections: [4500], growthRate: 2 },
        { id: 'expense-staff', name: 'Staff', category: 'fixed', projections: [3000], growthRate: 4 },
        { id: 'expense-supplies', name: 'Supplies', category: 'variable', projections: [1500], growthRate: 5 },
      ]);
      return;
    }
  };

  // Load existing forecast on mount
  useEffect(() => {
    loadForecast();
  }, [plan.id]);

  const loadForecast = async () => {
    if (!plan.id) return;
    
    try {
      const existingForecast = await getForecast(plan.id);
      if (existingForecast) {
        setForecast(existingForecast);
        // Load configuration into form
        setTimeline(existingForecast.timeline);
        setPeriodsCount(existingForecast.periodsCount);
        if (existingForecast.timeline === 'monthly' && existingForecast.periodsCount === 36) {
          setHorizonPreset('months_36');
        } else if (existingForecast.timeline === 'mixed') {
          setHorizonPreset('monthly12_yearly');
          setYearsAfterMonth12(Math.min(5, Math.max(3, existingForecast.periodsCount - 12)));
        } else {
          setHorizonPreset('custom');
        }
        setAssumptions(existingForecast.assumptions);
        setRevenueStreams(existingForecast.revenueStreams);
        setExpenseCategories(existingForecast.expenseCategories);
      }
    } catch (error) {
      console.error('Error loading forecast:', error);
    }
  };

  const handleCalculate = async () => {
    if (!plan.id || !plan.user_id) {
      toast.error('Cannot calculate forecast - missing plan data');
      return;
    }

    setIsCalculating(true);
    try {
      const input: ForecastCalculationInput = {
        timeline,
        periodsCount,
        startDate: new Date().toISOString().split('T')[0],
        assumptions,
        revenueStreams,
        expenseCategories
      };

      const calculatedForecast = calculateFinancialForecast(input);
      
      // Save to database
      const userResp = await supabase.auth.getUser();
      const userId = userResp?.data?.user?.id;
      if (!userId) {
        toast.error('You must be logged in');
        return;
      }

      const saved = await saveForecast(plan.id, userId, calculatedForecast);
      
      if (saved) {
        setForecast(saved);
        setIsEditing(false);
        toast.success('Financial forecast calculated successfully!');
      } else {
        toast.error('Failed to save forecast');
      }
    } catch (error) {
      console.error('Error calculating forecast:', error);
      toast.error('Failed to calculate forecast');
    } finally {
      setIsCalculating(false);
    }
  };

  const addRevenueStream = () => {
    const newStream: RevenueStream = {
      id: `revenue-${Date.now()}`,
      name: 'New Revenue Stream',
      category: 'product',
      projections: [],
      growthRate: 15,
      unitPrice: 0,
      unitsPerPeriod: 0
    };
    setRevenueStreams([...revenueStreams, newStream]);
  };

  const addExpenseCategory = () => {
    const newCategory: ExpenseCategory = {
      id: `expense-${Date.now()}`,
      name: 'New Expense',
      category: 'variable',
      projections: [],
      growthRate: 10
    };
    setExpenseCategories([...expenseCategories, newCategory]);
  };

  const updateRevenueStream = (index: number, updates: Partial<RevenueStream>) => {
    const updated = [...revenueStreams];
    updated[index] = { ...updated[index], ...updates };
    
    // Recalculate initial projection if unit economics change
    if (updates.unitPrice !== undefined || updates.unitsPerPeriod !== undefined) {
      updated[index].projections = [
        (updated[index].unitPrice || 0) * (updated[index].unitsPerPeriod || 0)
      ];
    }
    
    setRevenueStreams(updated);
  };

  const updateExpenseCategory = (index: number, updates: Partial<ExpenseCategory>) => {
    const updated = [...expenseCategories];
    updated[index] = { ...updated[index], ...updates };
    setExpenseCategories(updated);
  };

  const removeRevenueStream = (index: number) => {
    setRevenueStreams(revenueStreams.filter((_, i) => i !== index));
  };

  const removeExpenseCategory = (index: number) => {
    setExpenseCategories(expenseCategories.filter((_, i) => i !== index));
  };

  if (!forecast && !isEditing) {
    return (
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Financial Forecasting</h3>
              <p className="text-sm text-gray-500">Create detailed financial projections</p>
            </div>
          </div>
        </div>

        <div className="text-center py-8">
          <p className="text-gray-600 mb-4">
            No financial forecast yet. Create one to see revenue projections, expense forecasts, and break-even analysis.
          </p>
          <button
            onClick={() => setIsEditing(true)}
            className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 font-medium transition-colors"
          >
            Create Financial Forecast
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Financial Forecasting</h3>
            <p className="text-sm text-gray-500">
              {forecast ? formatForecastHorizon(forecast) : 'Configure your forecast'}
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          {isEditing ? 'Cancel' : 'Edit Forecast'}
        </button>
      </div>

      {isEditing ? (
        /* EDITING MODE */
        <div className="space-y-6">
          {/* Configuration Section */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-4">Forecast Configuration</h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Revenue model template</label>
                <select
                  value={selectedRevenueModel}
                  onChange={(e) => setSelectedRevenueModel(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Choose a template…</option>
                  <option value="unit_sales">Unit sales</option>
                  <option value="subscription">Subscription</option>
                  <option value="billable_hours">Billable hours</option>
                  <option value="occupancy">Occupancy</option>
                </select>
                <button
                  type="button"
                  onClick={() => {
                    if (!selectedRevenueModel) {
                      toast.error('Select a revenue model template first');
                      return;
                    }
                    applyRevenueModelTemplate(selectedRevenueModel);
                    toast.success('Template applied');
                  }}
                  className="mt-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
                >
                  Apply template
                </button>
              </div>
              <div className="md:col-span-2"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Forecast horizon</label>
                <select
                  value={horizonPreset}
                  onChange={(e) => setHorizonPreset(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="months_36">36 months (monthly)</option>
                  <option value="monthly12_yearly">12 months (monthly) + 3–5 years (yearly)</option>
                  <option value="custom">Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Years (3–5)</label>
                <input
                  type="number"
                  min="3"
                  max="5"
                  disabled={horizonPreset !== 'monthly12_yearly'}
                  value={yearsAfterMonth12}
                  onChange={(e) => setYearsAfterMonth12(parseInt(e.target.value) || 3)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={assumptions.taxRate}
                  onChange={(e) => setAssumptions({ ...assumptions, taxRate: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Loan amount</label>
                <input
                  type="number"
                  min="0"
                  value={(assumptions as any).loanAmount ?? 0}
                  onChange={(e) => setAssumptions({ ...(assumptions as any), loanAmount: parseFloat(e.target.value) || 0 } as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Interest rate (APR %)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={(assumptions as any).interestRate ?? 0}
                  onChange={(e) => setAssumptions({ ...(assumptions as any), interestRate: parseFloat(e.target.value) || 0 } as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Term (months)</label>
                <input
                  type="number"
                  min="0"
                  max="600"
                  value={(assumptions as any).loanTermMonths ?? 60}
                  onChange={(e) => setAssumptions({ ...(assumptions as any), loanTermMonths: parseInt(e.target.value) || 0 } as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">AR days</label>
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={(assumptions as any).arDays ?? 0}
                  onChange={(e) => setAssumptions({ ...(assumptions as any), arDays: parseInt(e.target.value) || 0 } as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">AP days</label>
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={(assumptions as any).apDays ?? 0}
                  onChange={(e) => setAssumptions({ ...(assumptions as any), apDays: parseInt(e.target.value) || 0 } as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Inventory days</label>
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={(assumptions as any).inventoryDays ?? 0}
                  onChange={(e) => setAssumptions({ ...(assumptions as any), inventoryDays: parseInt(e.target.value) || 0 } as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            {horizonPreset === 'custom' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Timeline</label>
                  <select
                    value={timeline}
                    onChange={(e) => setTimeline(e.target.value as TimelineType)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                    <option value="mixed">Mixed (12 monthly + yearly)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Periods</label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={periodsCount}
                    onChange={(e) => setPeriodsCount(parseInt(e.target.value) || 12)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
            ) : null}

            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h5 className="text-sm font-semibold text-gray-900">Capex items</h5>
                <button
                  type="button"
                  onClick={addCapexItem}
                  className="text-sm text-green-600 hover:text-green-700 font-medium"
                >
                  + Add capex item
                </button>
              </div>

              <div className="space-y-3">
                {(((assumptions as any).capexItems as any[]) || []).map((item, index) => (
                  <div key={item.id} className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                      <input
                        type="text"
                        value={item.name || ''}
                        onChange={(e) => updateCapexItem(index, { name: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      <input
                        type="number"
                        min="0"
                        value={item.amount ?? 0}
                        onChange={(e) => updateCapexItem(index, { amount: parseFloat(e.target.value) || 0 })}
                        className="px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      <input
                        type="number"
                        min="1"
                        value={item.purchasePeriodNumber ?? 1}
                        onChange={(e) => updateCapexItem(index, { purchasePeriodNumber: parseInt(e.target.value) || 1 })}
                        className="px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      <input
                        type="number"
                        min="1"
                        value={item.usefulLifeMonths ?? 36}
                        onChange={(e) => updateCapexItem(index, { usefulLifeMonths: parseInt(e.target.value) || 1 })}
                        className="px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeCapexItem(index)}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-red-600 shadow-sm transition hover:bg-gray-50"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-5 gap-3 text-xs text-gray-500">
                      <div></div>
                      <div>Amount</div>
                      <div>Purchase period #</div>
                      <div>Useful life (months)</div>
                      <div></div>
                    </div>
                  </div>
                ))}

                {(!((assumptions as any).capexItems as any[]) || ((assumptions as any).capexItems as any[]).length === 0) && (
                  <p className="text-sm text-gray-500">No capex items yet.</p>
                )}
              </div>
            </div>
          </div>

          {/* Revenue Streams */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-900">Revenue Streams</h4>
              <button
                onClick={addRevenueStream}
                className="text-sm text-green-600 hover:text-green-700 font-medium"
              >
                + Add Revenue Stream
              </button>
            </div>
            
            <div className="space-y-3">
              {revenueStreams.map((stream, index) => (
                <div key={stream.id} className="bg-green-50 rounded-lg p-4 border border-green-100">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input
                      type="text"
                      value={stream.name}
                      onChange={(e) => updateRevenueStream(index, { name: e.target.value })}
                      placeholder="Revenue name"
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <input
                      type="number"
                      value={stream.unitPrice || ''}
                      onChange={(e) => updateRevenueStream(index, { unitPrice: parseFloat(e.target.value) || 0 })}
                      placeholder="Unit price"
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <input
                      type="number"
                      value={stream.unitsPerPeriod || ''}
                      onChange={(e) => updateRevenueStream(index, { unitsPerPeriod: parseFloat(e.target.value) || 0 })}
                      placeholder="Units/period"
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={stream.growthRate}
                        onChange={(e) => updateRevenueStream(index, { growthRate: parseFloat(e.target.value) || 0 })}
                        placeholder="Growth %"
                        className="px-3 py-2 border border-gray-300 rounded-lg flex-1"
                      />
                      <button
                        onClick={() => removeRevenueStream(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {revenueStreams.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">No revenue streams yet. Add one to get started.</p>
              )}
            </div>
          </div>

          {/* Expense Categories */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-900">Expense Categories</h4>
              <button
                onClick={addExpenseCategory}
                className="text-sm text-red-600 hover:text-red-700 font-medium"
              >
                + Add Expense
              </button>
            </div>
            
            <div className="space-y-3">
              {expenseCategories.map((category, index) => (
                <div key={category.id} className="bg-red-50 rounded-lg p-4 border border-red-100">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input
                      type="text"
                      value={category.name}
                      onChange={(e) => updateExpenseCategory(index, { name: e.target.value })}
                      placeholder="Expense name"
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <select
                      value={category.category}
                      onChange={(e) => updateExpenseCategory(index, { category: e.target.value as any })}
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="fixed">Fixed</option>
                      <option value="variable">Variable</option>
                      <option value="one-time">One-time</option>
                    </select>
                    <input
                      type="number"
                      value={category.projections[0] || ''}
                      onChange={(e) => updateExpenseCategory(index, { projections: [parseFloat(e.target.value) || 0] })}
                      placeholder="Initial amount"
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={category.growthRate || 0}
                        onChange={(e) => updateExpenseCategory(index, { growthRate: parseFloat(e.target.value) || 0 })}
                        placeholder="Growth %"
                        className="px-3 py-2 border border-gray-300 rounded-lg flex-1"
                      />
                      <button
                        onClick={() => removeExpenseCategory(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {expenseCategories.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">No expense categories yet. Add one to get started.</p>
              )}
            </div>
          </div>

          {/* Calculate Button */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={() => setIsEditing(false)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCalculate}
              disabled={isCalculating || revenueStreams.length === 0}
              className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCalculating ? 'Calculating...' : 'Calculate Forecast'}
            </button>
          </div>
        </div>
      ) : forecast ? (
        /* RESULTS DISPLAY MODE */
        <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm text-gray-600">
              Exports are based on the saved forecast for this plan.
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  if (!forecast) return;
                  const csv = buildPnLCsv(forecast, activeScenario);
                  downloadCsv(`pnl_${plan.id}_${activeScenario}.csv`, csv);
                }}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
              >
                Export P&L CSV
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!forecast) return;
                  const csv = buildCashflowCsv(forecast, activeScenario);
                  downloadCsv(`cashflow_${plan.id}_${activeScenario}.csv`, csv);
                }}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
              >
                Export Cashflow CSV
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!forecast) return;
                  const csv = buildBalanceSheetCsv(forecast, activeScenario);
                  downloadCsv(`balance_sheet_${plan.id}_${activeScenario}.csv`, csv);
                }}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
              >
                Export Balance Sheet CSV
              </button>
            </div>
          </div>

          {/* Scenario Toggle */}
          <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
            {(['realistic', 'optimistic', 'pessimistic'] as ScenarioType[]).map((scenario) => (
              <button
                key={scenario}
                onClick={() => setActiveScenario(scenario)}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeScenario === scenario
                    ? 'bg-white text-gray-900 shadow'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {scenario.charAt(0).toUpperCase() + scenario.slice(1)}
              </button>
            ))}
          </div>

          {/* Key Metrics Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100">
              <div className="text-sm text-gray-600 mb-1">Gross Margin</div>
              <div className="text-2xl font-bold text-green-600">{forecast.metrics.grossMargin}%</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
              <div className="text-sm text-gray-600 mb-1">Net Margin</div>
              <div className="text-2xl font-bold text-blue-600">{forecast.metrics.netMargin}%</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-100">
              <div className="text-sm text-gray-600 mb-1">Revenue Growth</div>
              <div className="text-2xl font-bold text-purple-600">{forecast.metrics.revenueGrowthRate}%</div>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg p-4 border border-orange-100">
              <div className="text-sm text-gray-600 mb-1">Break-Even</div>
              <div className="text-2xl font-bold text-orange-600">{forecast.metrics.monthsToBreakEven} months</div>
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">Financial Projections</h4>
            <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('charts')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'charts'
                    ? 'bg-white text-gray-900 shadow'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                📊 Charts
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'table'
                    ? 'bg-white text-gray-900 shadow'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                📋 Table
              </button>
            </div>
          </div>

          {/* Charts View */}
          {viewMode === 'charts' && (
            <div className="space-y-6">
              {/* Metrics Dashboard */}
              <MetricsDashboard forecast={forecast} activeScenario={activeScenario} />
              
              {/* Revenue Chart */}
              <RevenueChart 
                data={forecast.projections[activeScenario]} 
                activeScenario={activeScenario}
              />
              
              {/* Two column layout for remaining charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ProfitChart 
                  data={forecast.projections[activeScenario]} 
                  activeScenario={activeScenario}
                />
                <ExpenseBreakdownChart 
                  expenses={forecast.expenseCategories}
                />
              </div>
            </div>
          )}

          {/* Table View */}
          {viewMode === 'table' && (
            <div className="overflow-x-auto">
              <p className="text-sm text-gray-600 mb-4">
                Showing <span className="font-semibold">{activeScenario}</span> scenario projections.
              </p>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setTableView('summary')}
                  className={
                    'rounded-lg px-3 py-2 text-xs font-semibold ring-1 ring-gray-200 transition ' +
                    (tableView === 'summary' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50')
                  }
                >
                  Summary
                </button>
                <button
                  type="button"
                  onClick={() => setTableView('pnl')}
                  className={
                    'rounded-lg px-3 py-2 text-xs font-semibold ring-1 ring-gray-200 transition ' +
                    (tableView === 'pnl' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50')
                  }
                >
                  P&L
                </button>
                <button
                  type="button"
                  onClick={() => setTableView('cashflow')}
                  className={
                    'rounded-lg px-3 py-2 text-xs font-semibold ring-1 ring-gray-200 transition ' +
                    (tableView === 'cashflow' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50')
                  }
                >
                  Cashflow
                </button>
                <button
                  type="button"
                  onClick={() => setTableView('balance_sheet')}
                  className={
                    'rounded-lg px-3 py-2 text-xs font-semibold ring-1 ring-gray-200 transition ' +
                    (tableView === 'balance_sheet' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50')
                  }
                >
                  Balance Sheet
                </button>
              </div>

              {tableView === 'summary' ? (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Period</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Revenue</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Expenses</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Net Profit</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Cash Flow</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getTableRows(forecast, activeScenario).map((proj, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-4">{proj.period}</td>
                        <td className="text-right py-2 px-4">${proj.totalRevenue.toLocaleString()}</td>
                        <td className="text-right py-2 px-4">${((proj.totalExpenses ?? 0) + (proj.depreciationExpense ?? 0)).toLocaleString()}</td>
                        <td className={`text-right py-2 px-4 font-medium ${proj.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ${proj.netProfit.toLocaleString()}
                        </td>
                        <td className="text-right py-2 px-4">${proj.cashFlow.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}

              {tableView === 'balance_sheet' ? (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Period</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Cash</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">AR</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Inventory</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">AP</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Total Assets</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Total Liabilities</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Total Equity</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Tie-out</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getTableRows(forecast, activeScenario).map((proj, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-4">{proj.period}</td>
                        <td className="text-right py-2 px-4">${(proj.cashBalance ?? 0).toLocaleString()}</td>
                        <td className="text-right py-2 px-4">${(proj.accountsReceivable ?? 0).toLocaleString()}</td>
                        <td className="text-right py-2 px-4">${(proj.inventory ?? 0).toLocaleString()}</td>
                        <td className="text-right py-2 px-4">${(proj.accountsPayable ?? 0).toLocaleString()}</td>
                        <td className="text-right py-2 px-4">${(proj.totalAssets ?? 0).toLocaleString()}</td>
                        <td className="text-right py-2 px-4">${(proj.totalLiabilities ?? 0).toLocaleString()}</td>
                        <td className="text-right py-2 px-4">${(proj.totalEquity ?? 0).toLocaleString()}</td>
                        <td className="text-right py-2 px-4">{(proj.balanceSheetTieOut ?? 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}

              {tableView === 'pnl' ? (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Period</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Revenue</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Expenses</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Gross Profit</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Tax</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Net Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getTableRows(forecast, activeScenario).map((proj, idx) => {
                      const tax = ((proj.grossProfit ?? 0) - (proj.interestExpense ?? 0)) - (proj.netProfit ?? 0);
                      const expenses = (proj.totalExpenses ?? 0) + (proj.depreciationExpense ?? 0);
                      return (
                        <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 px-4">{proj.period}</td>
                          <td className="text-right py-2 px-4">${(proj.totalRevenue ?? 0).toLocaleString()}</td>
                          <td className="text-right py-2 px-4">${expenses.toLocaleString()}</td>
                          <td className="text-right py-2 px-4">${(proj.grossProfit ?? 0).toLocaleString()}</td>
                          <td className="text-right py-2 px-4">${Math.round(tax).toLocaleString()}</td>
                          <td className={`text-right py-2 px-4 font-medium ${(proj.netProfit ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            ${(proj.netProfit ?? 0).toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : null}

              {tableView === 'cashflow' ? (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Period</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Cash Flow</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Cumulative Cash Flow</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getTableRows(forecast, activeScenario).map((proj, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-4">{proj.period}</td>
                        <td className={`text-right py-2 px-4 font-medium ${(proj.cashFlow ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ${(proj.cashFlow ?? 0) >= 0 ? '$' : '-$'}{Math.abs(proj.cashFlow ?? 0).toLocaleString()}
                        </td>
                        <td className="text-right py-2 px-4">${(proj.cumulativeCashFlow ?? 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}

              {forecast.projections[activeScenario].length > 12 && forecast.timeline !== 'mixed' && (
                <p className="text-sm text-gray-500 text-center mt-3">
                  Showing first 12 periods. Total: {forecast.projections[activeScenario].length} periods.
                </p>
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default FinancialForecastingPanel;
